/**
 * Wraps the authenticated app with idle-timeout enforcement and
 * server-driven session expiry handling.
 *
 * - When the user is idle past the warning threshold we toast a notice.
 * - When idle past the timeout, or when any API call returns 401, we
 *   POST /auth/logout to clear cookies and route to /login.
 * - While the user IS active we ping the server so the session slides even
 *   when they aren't generating API traffic (useSessionKeepalive).
 *
 * Server enforcement remains the authoritative check (middleware/
 * tokenVerify.js). This component is the UX layer.
 */

import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useIdleTimeout from '../hooks/useIdleTimeout';
import useSessionKeepalive from '../hooks/useSessionKeepalive';
import { apiPost } from '../api/client';
import { logoutRequest } from '../redux/modules/auth/authSlice';

export default function IdleTimeoutGate({ children }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isLoggedIn } = useSelector((s) => s.auth);

    const doLogout = useCallback(async (reason) => {
        try { await apiPost('auth/logout', {}); } catch { /* ignore */ }
        try { dispatch(logoutRequest()); } catch { /* ignore */ }
        if (reason) toast.info(reason);
        navigate('/login');
    }, [dispatch, navigate]);

    useIdleTimeout({
        enabled: isLoggedIn,
        onWarn: ({ msUntilTimeout }) => {
            const mins = Math.max(1, Math.round(msUntilTimeout / 60000));
            toast.warn(`You'll be signed out in about ${mins} minute${mins === 1 ? '' : 's'} due to inactivity.`);
        },
        onTimeout: () => doLogout('Signed out due to inactivity.'),
    });

    // Keeps a present-but-quiet user signed in. Reads the same activity
    // timestamp as the idle timer above, so it can never prop up a session
    // the idle policy would otherwise end.
    useSessionKeepalive({ enabled: isLoggedIn });

    // Server-side expiry from any api/client.js 401 → dispatched via window event
    useEffect(() => {
        if (!isLoggedIn) return undefined;
        const handler = () => doLogout('Your session has expired.');
        window.addEventListener('dwms:auth-expired', handler);
        return () => window.removeEventListener('dwms:auth-expired', handler);
    }, [isLoggedIn, doLogout]);

    return children;
}
